import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Modal,
  Snackbar,
  Alert,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import TerminalLayout from '../layout/TerminalLayout';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import routes from '../../routes/routes';
import { useFindProduct } from '../../hooks/useFindProduct';
import ProductImage from '../ui/ProductImage';
import { useWindowSize } from '../../hooks/useWindowSize';
import { useBarcodeScannerWaiting } from '../../hooks/useBarcodeScannerWaiting';
import { useAuth } from '../../hooks/useAuth';
import { config } from '../../config/config';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import {
  terminalBarIconButtonSx,
  terminalColors,
} from '../../theme/terminalStyles';
import BarcodeCameraDialog from '../picking/BarcodeCameraDialog';
import ProductPickerDialog from '../stocktake/ProductPickerDialog';
import StocktakeQuantityEditDialog from '../stocktake/StocktakeQuantityEditDialog';
import { buildLabelPayload } from '../../helpers/labelBarcode';
import { printLabels } from '../../helpers/labelPrintClient';
import { formatWeight, formatCount } from '../../helpers/dataFormatter';

const pageRootSx = {
  height: '100%',
  minHeight: 0,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxSizing: 'border-box',
  mx: { xs: -1.5, sm: -2 },
  px: { xs: 1.5, sm: 2 },
  background: `linear-gradient(180deg, ${terminalColors.gradientTop} 0%, ${terminalColors.gradientBottom} 100%)`,
};

const contentCardSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  borderRadius: 2,
  bgcolor: terminalColors.cardWhite,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  boxSizing: 'border-box',
};

const qtyTapSx = {
  cursor: 'pointer',
  textDecoration: 'underline',
  textUnderlineOffset: 4,
  textDecorationColor: terminalColors.accentOrange,
  fontWeight: 600,
  fontSize: { xs: '1.15rem', sm: '1.25rem' },
  color: terminalColors.textPrimary,
  py: 0.5,
};

const qtyDisabledSx = {
  ...qtyTapSx,
  cursor: 'default',
  textDecoration: 'none',
  color: 'text.disabled',
};

const NumericKeyboardModal = ({ open, handleClose, handleSearch, onRequestCamera }) => {
  const [inputData, setInputData] = useState('');
  const keyboardElement = useRef();

  const handleKeyboardInput = (inputValue) => {
    setInputData(inputValue);
  };

  const applyInput = () => {
    clearInput();
    handleSearch(inputData);
  };

  const clearInput = () => {
    setInputData('');
    if (keyboardElement.current) {
      keyboardElement.current.clearInput();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 400,
          bgcolor: terminalColors.gradientTop,
          boxShadow: 24,
          p: 2.5,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Введіть штрихкод товару
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          value={inputData}
          onChange={(e) => setInputData(e.target.value)}
          margin="normal"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: terminalColors.cardWhite } }}
        />

        <Keyboard
          keyboardRef={(r) => {
            keyboardElement.current = r;
          }}
          onChange={handleKeyboardInput}
          layout={{
            default: ['1 2 3', '4 5 6', '7 8 9', '{clearAll} 0 {bksp}'],
          }}
          display={{
            '{bksp}': '⌫',
            '{clearAll}': 'C',
          }}
          theme="hg-theme-default hg-layout-numeric"
          buttonTheme={[{ class: 'hg-red', buttons: '{clearAll}' }]}
          onKeyPress={(button) => {
            if (button === '{clearAll}') clearInput();
          }}
        />

        <TerminalOrangeButton
          fullWidth
          sx={{ mt: 1.5 }}
          startIcon={<PhotoCameraIcon />}
          onClick={() => {
            handleClose();
            onRequestCamera?.();
          }}
        >
          Сканувати з камери
        </TerminalOrangeButton>
        <TerminalOrangeButton
          fullWidth
          sx={{ mt: 1 }}
          startIcon={<SearchIcon />}
          onClick={applyInput}
          disabled={!inputData}
        >
          Пошук
        </TerminalOrangeButton>
      </Box>
    </Modal>
  );
};

const EmptyDataScan = () => (
  <Box
    sx={{
      ...contentCardSx,
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      py: { xs: 2, sm: 3 },
      gap: { xs: 1.5, sm: 2 },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: { xs: 140, sm: 180 },
        height: { xs: 140, sm: 180 },
        borderRadius: '50%',
        bgcolor: 'rgba(255, 179, 71, 0.22)',
        border: `3px solid ${terminalColors.accentOrange}`,
        flexShrink: 0,
      }}
    >
      <QrCodeScannerIcon
        sx={{
          fontSize: { xs: 88, sm: 108 },
          color: terminalColors.accentOrangeHover,
        }}
        aria-hidden
      />
    </Box>
    <Typography
      variant="h5"
      align="center"
      sx={{
        fontWeight: 700,
        color: terminalColors.textPrimary,
        fontSize: { xs: '1.35rem', sm: '1.75rem' },
        lineHeight: 1.25,
        px: 1,
      }}
    >
      Відскануйте товар
    </Typography>
  </Box>
);

const initPrintState = (product, printer) => {
  const isWeight = !!(product?.isWeightProduct || product?.isWeightProductNow);
  if (isWeight) {
    const q = Number(product?.quantityNow);
    return {
      modeEnabled: true,
      quantity: Number.isFinite(q) && q > 0 ? q : 1,
    };
  }
  if (product?.isKitProductNow) {
    const q = Math.max(1, Math.floor(Number(product?.quantityNow) || 1));
    return { modeEnabled: true, quantity: q };
  }
  const kitByDefault = !!printer?.kitByDefault;
  return {
    modeEnabled: kitByDefault,
    quantity: kitByDefault ? 1 : 0,
  };
};

const LabelPrinting = () => {
  const { isNonMobile } = useWindowSize();
  const { authState } = useAuth();
  const user = authState?.user;
  const printer = user?.terminalWorkplace?.printer;
  const generalSettings = user?.generalSettings || {};

  const { productData, findProductByBarcode, findProductById, resNotFound, setResNotFound } =
    useFindProduct();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [dKeyboardModalOpen, setDKeyboardModalOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [modeEnabled, setModeEnabled] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [labelCount, setLabelCount] = useState(1);
  const [qtyEditOpen, setQtyEditOpen] = useState(false);
  const [labelCountEditOpen, setLabelCountEditOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'error', message: '' });

  const scannerPaused = cameraOpen || catalogOpen || qtyEditOpen || labelCountEditOpen;
  useBarcodeScannerWaiting(config.scanCodeLength, !scannerPaused, [scannerPaused], {
    mthd: findProductByBarcode,
  });

  const isWeight = !!(productData?.isWeightProduct || productData?.isWeightProductNow);

  useEffect(() => {
    if (!productData?.name) return;
    const init = initPrintState(productData, printer);
    setModeEnabled(init.modeEnabled);
    setQuantity(init.quantity);
    setLabelCount(1);
  }, [productData, printer]);

  const handleModeChange = (checked) => {
    setModeEnabled(checked);
    if (!checked) {
      setQuantity(0);
    } else if (isWeight) {
      const q = Number(productData?.quantityNow);
      setQuantity(Number.isFinite(q) && q > 0 ? q : 1);
    } else {
      setQuantity((prev) => (prev > 0 ? prev : 1));
    }
  };

  const handleSearch = (inputData) => {
    setDKeyboardModalOpen(false);
    findProductByBarcode(inputData);
  };

  const handleCatalogSelect = async (product) => {
    setCatalogOpen(false);
    if (!product?.id) return;
    const barcode = String(product.barcode || '').trim();
    if (barcode) {
      await findProductByBarcode(barcode);
      return;
    }
    await findProductById(product.id);
  };

  const handleQtyConfirm = (raw) => {
    const next = String(raw || '').replace(',', '.');
    const n = Number(next);
    if (!Number.isFinite(n) || n <= 0) {
      setSnack({
        open: true,
        severity: 'error',
        message: isWeight ? 'Некоректна вага' : 'Некоректна кількість',
      });
      return;
    }
    setQuantity(isWeight ? n : Math.floor(n));
    setQtyEditOpen(false);
  };

  const handleLabelCountConfirm = (raw) => {
    const n = Math.floor(Number(String(raw || '').replace(',', '.')));
    if (!Number.isFinite(n) || n < 1) {
      setSnack({ open: true, severity: 'error', message: 'Некоректна кількість етикеток' });
      return;
    }
    setLabelCount(n);
    setLabelCountEditOpen(false);
  };

  const handlePrint = async () => {
    if (!productData?.name) return;
    if (modeEnabled) {
      const q = Number(quantity);
      if (!Number.isFinite(q) || q <= 0) {
        setSnack({
          open: true,
          severity: 'error',
          message: isWeight ? 'Вкажіть вагу' : 'Вкажіть кількість у комплекті',
        });
        return;
      }
    }
    const label = buildLabelPayload({
      product: productData,
      modeEnabled,
      isWeight,
      quantity,
      copies: labelCount,
      weightPrefix: generalSettings.weightBarcodePrefix,
      kitPrefix: generalSettings.kitBarcodePrefix,
    });
    if (!label.barcode) {
      setSnack({ open: true, severity: 'error', message: 'Немає штрихкоду для друку' });
      return;
    }
    setPrinting(true);
    try {
      const result = await printLabels(label, printer);
      setSnack({
        open: true,
        severity: 'success',
        message: result.mode === 'direct' ? 'Відправлено на принтер' : 'PDF відкрито для друку',
      });
    } catch (err) {
      setSnack({
        open: true,
        severity: 'error',
        message: err?.message || 'Помилка друку',
      });
    } finally {
      setPrinting(false);
    }
  };

  const searchToolbarActions = (
    <>
      <IconButton
        aria-label="каталог товарів"
        onClick={() => setCatalogOpen(true)}
        sx={terminalBarIconButtonSx}
      >
        <AddIcon />
      </IconButton>
      <IconButton
        aria-label="штрихкод"
        onClick={() => setDKeyboardModalOpen(true)}
        sx={terminalBarIconButtonSx}
      >
        <KeyboardIcon />
      </IconButton>
      <IconButton
        aria-label="камера"
        onClick={() => setCameraOpen(true)}
        sx={terminalBarIconButtonSx}
      >
        <PhotoCameraIcon />
      </IconButton>
    </>
  );

  const modeLabel = isWeight ? 'Друк вагового товару' : 'Друк комплекту';
  const qtyLabel = isWeight ? 'Вага' : 'Кількість у комплекті';
  const productImage = productData?.imageUrl ?? productData?.image;
  const productCode = String(productData?.code || productData?.article || '—').trim();
  const qtyDisplay = isWeight ? formatWeight(Number(quantity) || 0) : formatCount(Number(quantity) || 0);

  return (
    <TerminalLayout
      title="Друк етикеток"
      titleIcon={<LocalOfferOutlinedIcon />}
      backTo={routes.root}
      endActions={searchToolbarActions}
    >
      <Box sx={pageRootSx}>
        {productData?.name ? (
          <Box sx={{ ...contentCardSx, p: { xs: 1.5, sm: 2 }, gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1.5,
                alignItems: 'flex-start',
                flexShrink: 0,
              }}
            >
              <ProductImage
                image={productImage}
                placeholderCompact
                sx={{
                  width: { xs: 72, sm: 96 },
                  height: { xs: 72, sm: 96 },
                  flex: '0 0 auto',
                  maxHeight: 96,
                }}
                placeholderSx={{
                  width: { xs: 72, sm: 96 },
                  height: { xs: 72, sm: 96 },
                  my: 0,
                  py: 1,
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: terminalColors.textPrimary,
                    fontSize: { xs: '1.15rem', sm: '1.35rem' },
                    lineHeight: 1.25,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {productData.name}
                </Typography>
                <Typography sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.95rem' }}>
                  Код:{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: terminalColors.textPrimary }}>
                    {productCode}
                  </Box>
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={modeEnabled}
                    onChange={(e) => handleModeChange(e.target.checked)}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 600, fontSize: '1.05rem' }}>{modeLabel}</Typography>
                }
              />

              <Typography
                component="div"
                onClick={() => modeEnabled && setQtyEditOpen(true)}
                sx={modeEnabled ? qtyTapSx : qtyDisabledSx}
              >
                {qtyLabel}: <strong>{modeEnabled ? qtyDisplay : '—'}</strong>
              </Typography>

              <Typography
                component="div"
                onClick={() => setLabelCountEditOpen(true)}
                sx={qtyTapSx}
              >
                Кількість етикеток: <strong>{labelCount}</strong>
              </Typography>

              <TerminalOrangeButton
                fullWidth
                onClick={handlePrint}
                disabled={printing}
                sx={{ mt: 0.5, py: 1.5, fontSize: '1.15rem', fontWeight: 700 }}
              >
                {printing ? 'Друк…' : 'Друкувати'}
              </TerminalOrangeButton>
            </Box>
          </Box>
        ) : (
          <EmptyDataScan />
        )}
      </Box>

      <BarcodeCameraDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCode={(text) => {
          findProductByBarcode(text);
          setResNotFound(false);
        }}
        title="Сканувати штрихкод товару"
      />
      <NumericKeyboardModal
        open={dKeyboardModalOpen}
        handleClose={() => setDKeyboardModalOpen(false)}
        handleSearch={handleSearch}
        onRequestCamera={() => setCameraOpen(true)}
      />
      <ProductPickerDialog
        open={catalogOpen}
        standalone
        title="Вибрати товар"
        onClose={() => setCatalogOpen(false)}
        onSelectProduct={handleCatalogSelect}
      />
      <StocktakeQuantityEditDialog
        open={qtyEditOpen}
        initialValue={quantity}
        title={qtyLabel}
        onClose={() => setQtyEditOpen(false)}
        onConfirm={handleQtyConfirm}
      />
      <StocktakeQuantityEditDialog
        open={labelCountEditOpen}
        initialValue={labelCount}
        title="Кількість етикеток"
        onClose={() => setLabelCountEditOpen(false)}
        onConfirm={handleLabelCountConfirm}
      />
      <Snackbar
        open={resNotFound}
        autoHideDuration={3000}
        onClose={() => setResNotFound(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setResNotFound(false)}
          severity="error"
          sx={{
            fontSize: isNonMobile ? '1.25rem' : '1rem',
            fontWeight: 600,
          }}
        >
          Товар не знайдено
        </Alert>
      </Snackbar>
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ fontSize: isNonMobile ? '1.15rem' : '1rem', fontWeight: 600 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </TerminalLayout>
  );
};

export default LabelPrinting;
